import { APIResponseType } from 'app/apiService/types';
import apiService from '..';

interface UserPayload {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  roles: string[];
  dealer_group_id?: string; // Optional if not required
}

class User {
  static ENDPOINT = 'api/v2/users';

  static async postLogOutEvent(): Promise<APIResponseType> {
    try {
      const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}/logout`);

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async addUser(payload: UserPayload): Promise<APIResponseType> {
    try {
      const response = await apiService.post<APIResponseType>(`${this.ENDPOINT}`, payload);

      // Invalidate cache AFTER successful response
      await apiService.invalidateCacheByPrefix(`${this.ENDPOINT}`, 'GET');
      await apiService.invalidateCacheByPrefix(`${this.ENDPOINT}/datagrid?audit_meta.created_user_type=USER`);

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async deleteUser(userId: string): Promise<APIResponseType> {
    if (!userId) throw new Error("User ID is required for deletion");
    try {
      const response = await apiService.delete<APIResponseType>(`${this.ENDPOINT}/${userId}`);

      // Invalidate cache AFTER successful response
      await apiService.invalidateCacheByPrefix(`${this.ENDPOINT}/datagrid?audit_meta.created_user_type=USER`);

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async editUser(userId: string, payload: Partial<UserPayload>): Promise<APIResponseType> {
    if (!userId) throw new Error("User ID is required for editing");
    if (!Object.keys(payload).length) throw new Error("Edit payload cannot be empty");

    try {
      const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/${userId}`, payload);

      // Invalidate cache AFTER successful response
      await apiService.invalidateCacheByPrefix(`${this.ENDPOINT}`, 'GET');
      await apiService.invalidateCacheByPrefix(`${this.ENDPOINT}/datagrid?audit_meta.created_user_type=USER`);

      return response;
    } catch (error) {
      throw error;
    }
  }

  static async resetUserPassword(userId: string): Promise<APIResponseType> {
    if (!userId) throw new Error("User ID is required for password reset");
    try {
      const response = await apiService.put<APIResponseType>(`${this.ENDPOINT}/reset-password/${userId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  static async getUsersByDealerGroupId(dealerGroupId: string): Promise<APIResponseType> {
    if (!dealerGroupId) throw new Error("Dealer Group ID is required");
    try {
      const response = await apiService.get<APIResponseType>(`${this.ENDPOINT}/datagrid`, {
        'audit_meta.created_user_type': 'USER',
        'dealer_group_id': dealerGroupId
      });
      return response;
    } catch (error) {
      throw error;
    }
  }
}

export default User;
